import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Save, AlertCircle } from "lucide-react";
import { branchesApi, type CreateBranchDto, type BranchStatus } from "@/lib/api/branches";
import { BRANCHES_ACTIVE_QUERY_KEY } from "@/hooks/useActiveBranches";
import { cn } from "@/lib/utils";

const BranchFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<CreateBranchDto>({
    name: "",
    code: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    phone: "",
    email: "",
    isMain: false,
    status: "ACTIVE",
    permissions: [],
  });
  const isEditing = !!id;

  useEffect(() => {
    if (isTenantAdmin && isEditing && id) {
      loadBranch(id);
    }
  }, [isTenantAdmin, isEditing, id]);

  const loadBranch = async (branchId: string) => {
    try {
      setLoading(true);
      const branch = await branchesApi.getById(branchId);
      setFormData({
        name: branch.name,
        code: branch.code,
        address: branch.address || "",
        city: branch.city || "",
        state: branch.state || "",
        country: branch.country || "",
        postalCode: branch.postalCode || "",
        phone: branch.phone || "",
        email: branch.email || "",
        isMain: branch.isMain,
        status: branch.status,
        permissions: branch.permissions || [],
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load branch.");
      navigate("/setup/branches");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.code || !formData.code.trim()) {
      newErrors.code = "Code is required";
    } else if (formData.code.length < 2) {
      newErrors.code = "Code must be at least 2 characters";
    }

    setErrors(newErrors);
    setTouched(new Set(["name", "code"]));
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => new Set(prev).add(field));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    try {
      setSaving(true);
      if (isEditing && id) {
        await branchesApi.update(id, formData);
        toast.success(`"${formData.name}" has been updated successfully.`);
      } else {
        await branchesApi.create(formData);
        toast.success(`"${formData.name}" has been created successfully.`);
      }
      queryClient.invalidateQueries(BRANCHES_ACTIVE_QUERY_KEY);
      navigate("/setup/branches");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">You need administrator permissions to manage branches.</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading && isEditing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading branch..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title={isEditing ? "Edit Branch" : "Create Branch"}
          description={isEditing ? "Update branch information" : "Create a new library branch"}
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Branches", href: "/setup/branches" },
            { label: isEditing ? "Edit" : "New" },
          ]}
          showSearch={false}
        />

        <form onSubmit={handleSubmit}>
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Basic Information</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="name" className="flex items-center gap-1 mb-2">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onBlur={() => handleFieldBlur("name")}
                  placeholder="e.g., Main Library"
                  className={cn(
                    touched.has("name") && errors.name && "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("name") && errors.name && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="code" className="flex items-center gap-1 mb-2">
                  Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  onBlur={() => handleFieldBlur("code")}
                  placeholder="e.g., MAIN"
                  className={cn(
                    touched.has("code") && errors.code && "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("code") && errors.code && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.code}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="address" className="mb-2">
                  Address
                </Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address"
                />
              </div>

              <div>
                <Label htmlFor="city" className="mb-2">
                  City
                </Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                />
              </div>

              <div>
                <Label htmlFor="state" className="mb-2">
                  State/Province
                </Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="State"
                />
              </div>

              <div>
                <Label htmlFor="country" className="mb-2">
                  Country
                </Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Country"
                />
              </div>

              <div>
                <Label htmlFor="postalCode" className="mb-2">
                  Postal Code
                </Label>
                <Input
                  id="postalCode"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="Postal code"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="mb-2">
                  Phone
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>

              <div>
                <Label htmlFor="email" className="mb-2">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="branch@library.com"
                />
              </div>

              <div className="sm:col-span-2">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                  <Checkbox
                    id="isMain"
                    checked={formData.isMain}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, isMain: checked === true })
                    }
                  />
                  <div className="flex-1">
                    <Label htmlFor="isMain" className="font-medium cursor-pointer">
                      Main Branch
                    </Label>
                    <p className="text-xs text-gray-500">
                      Mark this as the primary branch for your library
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="status" className="mb-2">
                  Status
                </Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value as BranchStatus })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>
            </div>
          </Card>

          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => navigate("/setup/branches")}>
              Cancel
            </Button>
            <div className="flex gap-2">
              {isEditing && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/setup/branches/${id}/permissions`)}
                >
                  Manage Permissions
                </Button>
              )}
              <Button type="submit" disabled={saving} className="gap-2">
                {saving ? (
                  <>
                    <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {isEditing ? "Save Changes" : "Create Branch"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </PageContainer>
    </AppLayout>
  );
};

export default BranchFormPage;
